import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Layout } from '../../components/Layout/Layout';
import { Table } from '../../components/UI/Table';
import { Pagination } from '../../components/UI/Pagination';
import { Filters } from '../../components/UI/Filters';
import { SessionCallEntity, SessionCallStatus, SessionState, PaginatedResponse } from '../../types';
import { sessionCallsApi } from '../../services/api';
import { exportSessionCallsToExcel } from '../../utils/excelExport';
import { format } from 'date-fns';

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageHeader = styled.div`
  margin-bottom: 24px;
`;

const PageTitle = styled.h2`
  margin: 0 0 8px 0;
  font-size: 24px;
  color: var(--tg-theme-text-color, #000);
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const PageDescription = styled.p`
  margin: 0;
  color: var(--tg-theme-hint-color, #666);
  font-size: 14px;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  border: 1px solid ${props => props.variant === 'primary' ? 'var(--accent-primary)' : 'var(--border-secondary)'};
  border-radius: var(--radius-lg);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  background: ${props => props.variant === 'primary' 
    ? 'linear-gradient(135deg, var(--accent-primary) 0%, var(--primary-700) 100%)' 
    : 'var(--background-secondary)'};
  color: ${props => props.variant === 'primary' 
    ? '#ffffff' 
    : 'var(--text-primary)'};
  
  &:hover:not(:disabled) {
    background: ${props => props.variant === 'primary' 
      ? 'linear-gradient(135deg, var(--accent-hover) 0%, var(--primary-800) 100%)' 
      : 'var(--primary-50)'};
    border-color: ${props => props.variant === 'primary' ? 'var(--accent-hover)' : 'var(--accent-primary)'};
    transform: translateY(-2px);
    box-shadow: var(--shadow-md);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
    box-shadow: none !important;
  }
  
  @media (max-width: 768px) {
    padding: 10px 16px;
    font-size: 13px;
  }
`;

const BackButton = styled(ActionButton)`
  background: var(--background-tertiary);
  color: var(--text-secondary);
  border-color: var(--border-secondary);
  
  &:hover:not(:disabled) {
    background: var(--gray-200);
    color: var(--text-primary);
    border-color: var(--gray-300);
  }
`;

const StatusBadge = styled.span<{ status: SessionCallStatus }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: var(--radius-lg);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => {
    switch (props.status) {
      case SessionCallStatus.New: return 'var(--info-600)';
      case SessionCallStatus.Confirmed: return 'var(--success-600)';
      case SessionCallStatus.Canceled: return 'var(--gray-600)';
      case SessionCallStatus.Modified: return 'var(--warning-600)';
      case SessionCallStatus.NeedToCall: return '#d97706';
      case SessionCallStatus.Failed: return 'var(--error-600)';
      default: return 'var(--gray-600)';
    }
  }};
  background: ${props => {
    switch (props.status) {
      case SessionCallStatus.New: return 'var(--info-50)';
      case SessionCallStatus.Confirmed: return 'var(--success-50)';
      case SessionCallStatus.Canceled: return 'var(--gray-100)';
      case SessionCallStatus.Modified: return 'var(--warning-50)';
      case SessionCallStatus.NeedToCall: return '#fff7ed';
      case SessionCallStatus.Failed: return 'var(--error-50)';
      default: return 'var(--gray-100)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.status) {
      case SessionCallStatus.New: return 'var(--info-200)';
      case SessionCallStatus.Confirmed: return 'var(--success-200)';
      case SessionCallStatus.Canceled: return 'var(--gray-300)';
      case SessionCallStatus.Modified: return 'var(--warning-200)';
      case SessionCallStatus.NeedToCall: return '#fed7aa';
      case SessionCallStatus.Failed: return 'var(--error-200)';
      default: return 'var(--gray-300)';
    }
  }};
  
  &::before {
    content: ${props => {
      switch (props.status) {
        case SessionCallStatus.New: return '"🆕"';
        case SessionCallStatus.Confirmed: return '"✅"';
        case SessionCallStatus.Canceled: return '"❌"';
        case SessionCallStatus.Modified: return '"📝"';
        case SessionCallStatus.NeedToCall: return '"📞"';
        case SessionCallStatus.Failed: return '"💥"';
        default: return '"❓"';
      }
    }};
    font-size: 10px;
  }
`;

const StateBadge = styled.span<{ state: SessionState }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: var(--radius-lg);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => {
    switch (props.state) {
      case SessionState.Pending: return 'var(--warning-600)';
      case SessionState.InProgress: return 'var(--info-600)';
      case SessionState.Done: return 'var(--success-600)';
      case SessionState.Canceled: return 'var(--gray-600)';
      case SessionState.Failed: return 'var(--error-600)';
      default: return 'var(--gray-600)';
    }
  }};
  background: ${props => {
    switch (props.state) {
      case SessionState.Pending: return 'var(--warning-50)';
      case SessionState.InProgress: return 'var(--info-50)';
      case SessionState.Done: return 'var(--success-50)';
      case SessionState.Canceled: return 'var(--gray-100)';
      case SessionState.Failed: return 'var(--error-50)';
      default: return 'var(--gray-100)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.state) {
      case SessionState.Pending: return 'var(--warning-200)';
      case SessionState.InProgress: return 'var(--info-200)';
      case SessionState.Done: return 'var(--success-200)';
      case SessionState.Canceled: return 'var(--gray-300)';
      case SessionState.Failed: return 'var(--error-200)';
      default: return 'var(--gray-300)';
    }
  }};
  
  &::before {
    content: ${props => {
      switch (props.state) {
        case SessionState.Pending: return '"⏳"';
        case SessionState.InProgress: return '"🔄"';
        case SessionState.Done: return '"✅"';
        case SessionState.Canceled: return '"⏹️"';
        case SessionState.Failed: return '"💥"';
        default: return '"❓"';
      }
    }};
    font-size: 10px;
  }
`;

const getStatusLabel = (status: SessionCallStatus): string => {
  switch (status) {
    case SessionCallStatus.New: return 'New';
    case SessionCallStatus.Confirmed: return 'Confirmed';
    case SessionCallStatus.Canceled: return 'Canceled';
    case SessionCallStatus.Modified: return 'Modified';
    case SessionCallStatus.NeedToCall: return 'Need To Call';
    case SessionCallStatus.Failed: return 'Failed';
    default: return 'Unknown';
  }
};

const getStateLabel = (state: SessionState): string => {
  switch (state) {
    case SessionState.Pending: return 'Pending';
    case SessionState.InProgress: return 'In Progress';
    case SessionState.Done: return 'Done';
    case SessionState.Canceled: return 'Canceled';
    case SessionState.Failed: return 'Failed';
    default: return 'Unknown';
  }
};

export const SessionCalls: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const [sessionCalls, setSessionCalls] = useState<SessionCallEntity[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [statusFilter, setStatusFilter] = useState<number | string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<string>('appointmentTime');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  const pageSize = 10;

  const statusOptions = [
    { value: SessionCallStatus.New, label: 'New' },
    { value: SessionCallStatus.Confirmed, label: 'Confirmed' },
    { value: SessionCallStatus.Canceled, label: 'Canceled' },
    { value: SessionCallStatus.Modified, label: 'Modified' },
    { value: SessionCallStatus.NeedToCall, label: 'Need To Call' },
    { value: SessionCallStatus.Failed, label: 'Failed' }
  ];

  const columns = [
    {
      key: 'id',
      title: 'ID',
      sortable: true
    },
    {
      key: 'state',
      title: 'State',
      sortable: true,
      render: (state: SessionState) => (
        <StateBadge state={state}>
          {getStateLabel(state)}
        </StateBadge>
      )
    },
    {
      key: 'status',
      title: 'Status',
      sortable: true,
      render: (status: SessionCallStatus) => (
        <StatusBadge status={status}>
          {getStatusLabel(status)}
        </StatusBadge>
      )
    },
    {
      key: 'patientName',
      title: 'Patient',
      sortable: true,
      render: (_: any, record: SessionCallEntity) => 
        `${record.patientName} ${record.patientLastName}`
    },
    {
      key: 'drName',
      title: 'Doctor',
      sortable: true
    },
    {
      key: 'patientPhone',
      title: 'Phone',
      sortable: true
    },
    {
      key: 'appointmentTime',
      title: 'Appointment',
      sortable: true,
      render: (appointmentTime: string) => format(new Date(appointmentTime), 'MMM dd, HH:mm')
    },
    {
      key: 'destination',
      title: 'Destination',
      sortable: true
    }
  ];

  const fetchSessionCalls = async () => {
    if (!sessionId) return;
    
    try {
      setLoading(true);
      const response: PaginatedResponse<SessionCallEntity> = await sessionCallsApi.getBySessionId(
        Number(sessionId),
        {
          page: currentPage,
          pageSize,
          callStatus: statusFilter === '' ? undefined : Number(statusFilter),
          search: searchTerm || undefined,
          sortLabel: sortBy || undefined,
          sortDirection: sortDirection === 'asc' ? 'Asc' : 'Desc'
        }
      );
      
      setSessionCalls(response.data);
      setTotalPages(response.totalPages);
      setTotalItems(response.totalCount);
    } catch (error) {
      console.error('Error fetching session calls:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessionCalls();
  }, [sessionId, currentPage, statusFilter, searchTerm, sortBy, sortDirection]);

  useEffect(() => {
    if (searchTerm) {
      const timeoutId = setTimeout(() => {
        setCurrentPage(1);
        fetchSessionCalls();
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm]);

  const handleSort = (key: string) => {
    if (sortBy === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(key);
      setSortDirection('asc');
    }
  };

  const handleClearFilters = () => {
    setStatusFilter('');
    setSearchTerm('');
    setCurrentPage(1);
  };

  const handleStatusChange = (status: number | string) => {
    setStatusFilter(status);
    setCurrentPage(1);
  };

  const handleSearchChange = (search: string) => {
    setSearchTerm(search);
    setCurrentPage(1);
  };

  const handleExportToExcel = async () => {
    setExporting(true);
    try {
      const success = exportSessionCallsToExcel(
        sessionCalls,
        `session-${sessionId}-calls`
      );
      if (!success) {
        alert('Failed to export data to Excel');
      }
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export data to Excel');
    } finally {
      setExporting(false);
    }
  };

  const handleBackToSessions = () => {
    navigate('/sessions');
  };

  if (!sessionId) {
    return (
      <Layout title="Session Calls">
        <Container>
          <div>Invalid session ID</div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title="Session Calls">
      <Container>
        <PageHeader>
          <PageTitle>Session Calls - Session #{sessionId}</PageTitle>
          <PageDescription>Detailed view of session calls with filtering and export options</PageDescription>
        </PageHeader>
        
        <ActionButtons>
          <BackButton onClick={handleBackToSessions}>
            ← Back to Sessions
          </BackButton>
          <ActionButton 
            variant="primary" 
            onClick={handleExportToExcel}
            disabled={exporting || sessionCalls.length === 0}
          >
            {exporting ? 'Exporting...' : '📊 Export to Excel'}
          </ActionButton>
        </ActionButtons>
        
        <Filters
          statusFilter={statusFilter}
          searchTerm={searchTerm}
          statusOptions={statusOptions}
          onStatusChange={handleStatusChange}
          onSearchChange={handleSearchChange}
          onClear={handleClearFilters}
        />
        
        <Table
          columns={columns}
          data={sessionCalls}
          loading={loading}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSort={handleSort}
        />
        
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          totalItems={totalItems}
          itemsPerPage={pageSize}
          onPageChange={setCurrentPage}
        />
      </Container>
    </Layout>
  );
};